import { useEffect, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  FormattedMessage,
  Modal,
} from '@b-stock/fe-core/bstock-react'
import type { SavedSearchDto } from '@b-stock/saved-search-api-client'

import { ModalContent, ModalFooter } from '@/components/common/modal/ui'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useListingSearch } from '../ListingSearchProvider'
import { useSavedSearch } from './SavedSearchContext'
import SavedSearchItem from './SavedSearchItem'
import { ModalEmptyState, SearchList } from './ui'

const SearchModalContent = styled(ModalContent)`
  min-height: 5rem;
`

type SavedSearchModal = {
  closeModal: () => void
  isMobile?: boolean
}

const SavedSearchModal = ({
  closeModal,
  isMobile = false,
}: SavedSearchModal) => {
  const { trackButtonClicked } = useAnalytics()
  const savedSearchHeaderId = 'shoppingListTitle'

  const { savedSearches, savedSearchId } = useSavedSearch()
  const { setSavedSearch } = useListingSearch()

  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [selected, setSelected] = useState<SavedSearchDto['_id'] | null>(
    savedSearchId
  )

  useEffect(() => {
    setSubmitDisabled(selected === null || savedSearches.length === 0) // oxlint-disable-line react/set-state-in-effect
  }, [selected, savedSearches.length])

  enum ButtonName {
    SHOW_SAVED_SEARCH_RESULTS = 'show_saved_search_results',
  }

  const applySelected = () => {
    trackButtonClicked({
      screenName: 'all_auction',
      buttonName: ButtonName.SHOW_SAVED_SEARCH_RESULTS,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      saved_search_id: selected,
    })
    if (!selected) return
    setSavedSearch(selected)
    closeModal()
  }

  return (
    <Modal closeModal={closeModal}>
      <Modal.Header>
        <FormattedMessage
          id={`SavedSearch.${savedSearchHeaderId}`}
          values={{ count: savedSearches.length }}
        />
      </Modal.Header>

      <SearchModalContent $size="tall">
        <SearchList>
          {savedSearches.length === 0 ? (
            <ModalEmptyState
              icon={faSearch}
              size={2}
              title={
                <FormattedMessage id="SavedSearch.modal.emptyState.title" />
              }
              description={
                <FormattedMessage id="SavedSearch.modal.emptyState.description" />
              }
            />
          ) : (
            savedSearches.map((ss) => (
              <SavedSearchItem
                key={ss._id}
                {...ss}
                selected={selected === ss._id}
                setSelected={setSelected}
              />
            ))
          )}
        </SearchList>
      </SearchModalContent>

      <ModalFooter>
        {/* These buttons are in manually because Modal.Actions sends the same
          `disabled` prop to both the cancel and the submit buttons */}
        <Modal.Actions alignment={isMobile ? 'firstOnLeft' : 'end'}>
          <Button
            type="button"
            onClick={closeModal}
            size="medium"
            appearance="tertiary"
          >
            <FormattedMessage id="SavedSearch.modal.btn.close" />
          </Button>

          <Button
            type="button"
            onClick={applySelected}
            size="medium"
            disabled={submitDisabled}
          >
            <FormattedMessage id="Common.showResults" />
          </Button>
        </Modal.Actions>
      </ModalFooter>
    </Modal>
  )
}

export default SavedSearchModal
